<!--
  Aerial and Scissor Lift Inspection Form
  
  Step-by-step equipment inspection with:
  - 4 steps: Basic Info, Check Items, Ground Controls, Platform Controls
  - Radio buttons for condition/operation verification
  - Conditional signature requirement
  - All fields required except "otherRepairs" (optional)

  @author MacSafety Development Team
  @version 1.0
  @access All authenticated users
-->

<!-- Use the form layout component to handle the wizard UI -->
<app-form-layout
  [title]="formTitle"
  [form]="form"
  [icon]="formIcon"
  [disabled]="submitting"
  [onSubmit]="onSubmit.bind(this)"
  [steps]="steps"
  [currentStep]="currentStep()"
  [completedSteps]="completedSteps()"
  [canNavigateToStep]="canNavigateToStep.bind(this)"
  [isStepCompleted]="isStepCompleted.bind(this)"
  [onNextStep]="onNextStep.bind(this)"
  [onPreviousStep]="onPreviousStep.bind(this)"
  [getProgressPercentage]="getProgressPercentage.bind(this)"
>
  <div *ngIf="form" formContent [formGroup]="form" class="form-content">
    
    <!-- Basic Information Step - Equipment identification and inspector details -->
    <div *ngIf="currentStep() === steps[0].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label >
            <h1>{{ steps[0].title }}</h1>
            <p>{{ steps[0].description }}</p>
          </ion-label>
          </ion-item-divider>
        
         
     
        <ion-grid>
          <!-- Location and unit information -->
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('location')!"
                [label]="fieldMappings['location']"
                [requiredField]="requiredFields['location']"
                placeholder="Enter location"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('unitNumber')!"
                [label]="fieldMappings['unitNumber']"
                [requiredField]="requiredFields['unitNumber']"
                placeholder="Enter unit number"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <!-- Equipment type and job details -->
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('typeOfEquipment')!"
                [label]="fieldMappings['typeOfEquipment']"
                [requiredField]="requiredFields['typeOfEquipment']"
                placeholder="Enter type of equipment"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('jobNumber')!"
                [label]="fieldMappings['jobNumber']"
                [requiredField]="requiredFields['jobNumber']"
                placeholder="Enter job number"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <!-- Inspector and date -->
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('inspectedBy')!"
                [label]="fieldMappings['inspectedBy']"
                [requiredField]="requiredFields['inspectedBy']"
                placeholder="Enter inspector name"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-date-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('date')!"
                [label]="fieldMappings['date']"
                [requiredField]="requiredFields['date']"
                [today]="true"
                class="custom-datetime"
              >
              </app-date-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Equipment Condition Checks Step - All equipment safety checks with radio buttons -->
    <div *ngIf="currentStep() === steps[1].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[1].title }}</h1>
            <p>{{ steps[1].description }}</p>
          </ion-label>
        </ion-item-divider>
         
     
        <ion-grid>
          <!-- Fuel, oil, hydraulics, battery, tires, structure, placards, safety equipment, tilt alarm -->
          <ion-row
            *ngFor="
              let field of [
                'fuelLevel',
                'engineOilLevel',
                'hydraulicSystemLevel',
                'batteryCondition',
                'tiresCondition',
                'platformStructure',
                'instructionPlacards',
                'eyeWashFireExtinguisher',
                'tiltAlarm'
              ]
            "
          >
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="checkItemsFormGroup"
                [control]="checkItemsFormGroup.get(field)!"
                [label]="fieldMappings[field]"
                [requiredField]="requiredFields[field]"
                [options]="['Satisfactory', 'Needs Repair', 'N/A']"
                [orientation]="'vertical'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Ground Control Station Step - Ground-level operation verification -->
    <div *ngIf="currentStep() === steps[2].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label >
            <h1>{{ steps[2].title }}</h1>
            <p>{{ steps[2].description }}</p>
          </ion-label>
        </ion-item-divider>
        <ion-grid>
          <!-- Raise/lower, auxiliary power, telescope, swing controls -->
          <ion-row
            *ngFor="
              let field of [
                'groundRaiseLowerPlatform',
                'groundAuxiliaryPower',
                'groundTelescopeOutIn',
                'groundSwingRightLeft'
              ]
            "
          >
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="groundControlFormGroup"
                [control]="groundControlFormGroup.get(field)!"
                [label]="fieldMappings[field]"
                [requiredField]="requiredFields[field]"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Platform Control Station Step - Platform-level controls and final inspection -->
    <div *ngIf="currentStep() === steps[3].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[3].title }}</h1>
            <p>{{ steps[3].description }}</p>
          </ion-label>
        </ion-item-divider>
        <ion-grid>
          <!-- Safety harness, telescope, raise/lower, swing, outriggers, drive, auxiliary power, area -->
          <ion-row
            *ngFor="
              let field of [
                'platformSafetyHarness',
                'platformTelescopeOutIn',
                'platformRaiseLowerPlatform',
                'platformSwingRightLeft',
                'extendLevelOutriggers',
                'driveMachineForwardReverse',
                'platformAuxiliaryPower',
                'areaInspected'
              ]
            "
          >
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="platformControlFormGroup"
                [control]="platformControlFormGroup.get(field)!"
                [label]="fieldMappings[field]"
                [requiredField]="requiredFields[field]"
                [options]="['Yes', 'No', 'N/A']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <!-- Optional repair notes -->
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="platformControlFormGroup"
                [control]="platformControlFormGroup.get('otherRepairs')!"
                [label]="fieldMappings['otherRepairs']"
                [rows]="3"
                placeholder="List other repairs if necessary"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
          <!-- Conditional signature requirement -->
          <ion-row *ngIf="formAttachmentRequirements!.signatures![0]">
            <app-signature-section
              [signatureInfo]="formAttachmentRequirements!.signatures![0]!"
            ></app-signature-section>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>
  </div>
</app-form-layout>
